import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { placeLabel } from '../lib/placeLabel';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Phone, MessageSquare, MessageCircle, Navigation2, Plane,
  MapPin, Clock, Users, Briefcase, Car, IdCard, CheckCircle2,
  Radio, AlertCircle, X, ChevronRight, Loader2, ArrowRight, Copy, ArrowLeft, LogOut,
  Plus, Trash2, Timer, CalendarPlus,
} from 'lucide-react';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { toE164, formatDisplay, toWhatsAppDigits } from '../lib/phone';
import { formatUkDate } from '../lib/date';
import { driverApi, isDriverSignedIn, driverLogout } from '../lib/driverAuth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_LIBRARIES = ['places'];

// Status flow (in order). A status is enabled only when the previous one is set.
const STATUS_FLOW = [
  { key: 'on_the_way', label: 'On The Way',     short: 'OTW',    next: 'I am on my way to pick up the customer' },
  { key: 'arrived',    label: 'Arrived',         short: 'Arrived', next: 'I have arrived at the pickup point' },
  { key: 'pob',        label: 'Passenger On Board', short: 'POB',    next: 'Passenger is now in the car' },
  { key: 'completed',  label: 'Journey Complete', short: 'Done',   next: 'The journey has been completed' },
];

const statusOrder = STATUS_FLOW.map(s => s.key);

// — Helpers ————————————————————————————————————————————————————————

const cleanFlightNumber = (raw = '') => (raw || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

const flightRadarUrl = (raw = '') => {
  const fn = cleanFlightNumber(raw || '');
  return fn ? `https://www.flightradar24.com/data/flights/${fn.toLowerCase()}` : null;
};

const whatsappUrl = (phoneRaw = '', message = '') => {
  const digits = toWhatsAppDigits(phoneRaw);
  if (!digits) return null;
  const text = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${digits}${text}`;
};

const navUrls = (destination, origin = '') => {
  const enc = (s) => encodeURIComponent(s || '');
  return {
    google: origin
      ? `https://www.google.com/maps/dir/?api=1&destination=${enc(destination)}&origin=${enc(origin)}&travelmode=driving`
      : `https://www.google.com/maps/dir/?api=1&destination=${enc(destination)}&travelmode=driving`,
    apple:  origin
      ? `https://maps.apple.com/?saddr=${enc(origin)}&daddr=${enc(destination)}&dirflg=d`
      : `https://maps.apple.com/?daddr=${enc(destination)}&dirflg=d`,
    waze:   `https://waze.com/ul?q=${enc(destination)}&navigate=yes`,
  };
};

const staticMapUrl = (pickup, dropoff, apiKey) => {
  if (!apiKey) return null;
  const params = new URLSearchParams({
    size: '600x260',
    scale: '2',
    maptype: 'roadmap',
    markers: `color:0x00C853|label:A|${pickup}`,
    key: apiKey,
  });
  params.append('markers', `color:0xD32F2F|label:B|${dropoff}`);
  params.append('path', `color:0x000000|weight:4|${pickup}|${dropoff}`);
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
};

// Clipboard helper with fallback for older browsers / non-secure contexts.
const copyToClipboard = async (text, label = 'Copied') => {
  if (!text) return;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    toast.success(`${label} copied`);
  } catch {
    toast.error('Could not copy');
  }
};

// — Sub components —————————————————————————————————————————————————

const ContactRow = ({ name, phone }) => {
  const e164 = toE164(phone);
  const tel = e164 ? `tel:${e164}` : null;
  const sms = e164 ? `sms:${e164}` : null;
  const wa  = whatsappUrl(phone, '');
  const display = phone ? formatDisplay(phone) : '';
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-5">
      <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Customer</p>
      <button
        type="button"
        onClick={() => copyToClipboard(name || '', 'Name')}
        disabled={!name}
        className="text-left w-full -mx-1 px-1 py-0.5 rounded-md hover:bg-neutral-50 active:bg-neutral-100 disabled:cursor-default group"
        data-testid="dp-copy-name"
        title="Tap to copy"
      >
        <span className="inline-flex items-center gap-1.5 text-lg sm:text-xl font-semibold text-black break-words">
          <span data-testid="dp-customer-name">{name || 'Passenger'}</span>
          {name && <Copy className="h-3.5 w-3.5 text-neutral-300 group-hover:text-neutral-500 shrink-0" />}
        </span>
      </button>
      {phone && (
        <button
          type="button"
          onClick={() => copyToClipboard(e164 || phone, 'Number')}
          className="text-left w-full -mx-1 px-1 py-0.5 mt-0.5 rounded-md hover:bg-neutral-50 active:bg-neutral-100 group"
          data-testid="dp-copy-phone"
          title="Tap to copy"
        >
          <span className="inline-flex items-center gap-1.5 text-sm text-neutral-600">
            <span data-testid="dp-customer-phone">{display}</span>
            <Copy className="h-3 w-3 text-neutral-300 group-hover:text-neutral-500 shrink-0" />
          </span>
        </button>
      )}
      {phone && (
        <div className="grid grid-cols-3 gap-2 mt-4">
          <a href={tel} className="contact-pill bg-black text-white" data-testid="dp-btn-call">
            <Phone className="h-4 w-4" /> Call
          </a>
          <a href={sms} className="contact-pill bg-neutral-100 text-black border border-neutral-200" data-testid="dp-btn-sms">
            <MessageSquare className="h-4 w-4" /> SMS
          </a>
          {wa && (
            <a href={wa} target="_blank" rel="noreferrer" className="contact-pill bg-[#25D366] text-white" data-testid="dp-btn-whatsapp">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const NavSheet = ({ open, onClose, destination, urls }) => {
  if (!open) return null;
  const items = [
    { key: 'google', label: 'Google Maps', sub: 'Default for most Android phones',  url: urls.google, accent: '#1A73E8' },
    { key: 'apple',  label: 'Apple Maps',  sub: 'iOS native navigation',             url: urls.apple,  accent: '#000000' },
    { key: 'waze',   label: 'Waze',        sub: 'Live traffic, community alerts',    url: urls.waze,   accent: '#33CCFF' },
  ];
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="dp-nav-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 pb-8 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-5 gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Navigate to</p>
            <p className="text-lg font-semibold text-black break-words" data-testid="dp-nav-destination">{destination}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full shrink-0" aria-label="Close" data-testid="dp-nav-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-2">
          {items.map((it) => (
            <a
              key={it.key}
              href={it.url}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="flex items-center gap-4 p-4 rounded-2xl border border-neutral-200 hover:border-black hover:bg-neutral-50 transition-all"
              data-testid={`dp-nav-${it.key}`}
            >
              <div
                className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: it.accent }}
              >
                <Navigation2 className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-black">{it.label}</p>
                <p className="text-xs text-neutral-500 truncate">{it.sub}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-neutral-400" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const NameSignOverlay = ({ name, onClose }) => {
  const [showHint, setShowHint] = useState(true);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => setShowHint(false), 4000);
    return () => {
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center px-6" data-testid="dp-name-sign">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 text-white/80 hover:text-white z-10"
        aria-label="Close"
        data-testid="dp-name-sign-close"
      >
        <X className="h-6 w-6" />
      </button>
      <p className="text-white/50 uppercase tracking-[0.4em] text-xs mb-6">Obsidian Chauffeurs</p>
      <p className="text-white font-bold text-center leading-[1.05]"
         style={{ fontSize: 'clamp(48px, 12vw, 160px)', letterSpacing: '-0.02em' }}>
        {name || 'Welcome'}
      </p>
      <p
        className={`text-white/40 text-center text-xs mt-10 max-w-md transition-opacity duration-700 ${showHint ? 'opacity-100' : 'opacity-0'}`}
      >
        Tap anywhere to dismiss
      </p>
      <div className="absolute inset-0" onClick={onClose} />
    </div>
  );
};

const StatusButton = ({ s, currentIdx, idx, onClick, busy }) => {
  const done    = idx <  currentIdx;
  const active  = idx === currentIdx;
  const enabled = idx <= currentIdx + 1 && !busy && !(idx === currentIdx);
  const next    = idx === currentIdx + 1;

  let cls = 'group w-full px-5 py-4 rounded-2xl flex items-center gap-4 transition-all text-left';
  if (done)         cls += ' bg-emerald-50 border border-emerald-200';
  else if (active)  cls += ' bg-black text-white shadow-lg';
  else if (next)    cls += ' bg-white border-2 border-black hover:bg-black hover:text-white';
  else              cls += ' bg-neutral-50 border border-neutral-200 text-neutral-400 cursor-not-allowed';

  return (
    <button
      type="button"
      onClick={() => enabled && onClick(s.key)}
      disabled={!enabled}
      className={cls}
      data-testid={`dp-status-${s.key}`}
    >
      <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 font-bold text-sm
        ${done ? 'bg-emerald-500 text-white' :
          active ? 'bg-white text-black' :
          next ? 'bg-black text-white group-hover:bg-white group-hover:text-black' :
          'bg-neutral-200 text-neutral-400'}`}>
        {done ? <CheckCircle2 className="h-5 w-5" /> : idx + 1}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold">{s.label}</p>
        <p className={`text-xs mt-0.5 ${done ? 'text-emerald-700' : active ? 'text-white/70' : next ? 'text-neutral-600 group-hover:text-white/70' : 'text-neutral-400'}`}>
          {done ? 'Completed' : active ? 'Current status' : next ? s.next : 'Locked'}
        </p>
      </div>
      {active && (
        <span className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-white/15 backdrop-blur">
          <Radio className="h-3 w-3" /> LIVE
        </span>
      )}
      {next && !busy && <ArrowRight className="h-5 w-5 shrink-0" />}
      {busy && next && <Loader2 className="h-5 w-5 animate-spin shrink-0" />}
    </button>
  );
};

// — Page ———————————————————————————————————————————————————————————

const DriverPortal = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  // Capture the deep-link target once at mount so StrictMode's double-effect
  // doesn't replace it with /driver/login after the first redirect.
  const deepLinkRef = useRef(window.location.pathname);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [navDestination, setNavDestination] = useState(null);
  const [showSign, setShowSign] = useState(false);
  const [gpsState, setGpsState] = useState('inactive'); // 'inactive' | 'starting' | 'active' | 'denied' | 'unsupported'
  const [extras, setExtras] = useState({ waitingMinutes: '', extraMiles: '', notes: '', stops: [] });
  const [savingExtras, setSavingExtras] = useState(false);
  const stopAcRefs = useRef({});
  const { isLoaded: gmapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GMAPS_LIBRARIES,
  });
  const watchRef = useRef(null);
  const lastPingRef = useRef(0);

  // Redirect to login if no token (deep-link safe)
  useEffect(() => {
    if (!isDriverSignedIn()) {
      const captured = deepLinkRef.current || window.location.pathname;
      const safeNext = captured.startsWith('/driver/login') ? '/driver' : captured;
      navigate(`/driver/login?next=${encodeURIComponent(safeNext)}`, { replace: true });
    }
  }, [navigate]);

  const load = useCallback(async () => {
    if (!isDriverSignedIn()) return; // login redirect in flight
    try {
      const { data: res } = await driverApi.get(`/driver/booking/${bookingId}`);
      setData(res);
      const te = res?.booking?.tripExtras;
      if (te) {
        setExtras({
          waitingMinutes: te.waitingMinutes ? String(te.waitingMinutes) : '',
          extraMiles: te.extraMiles ? String(te.extraMiles) : '',
          notes: te.notes || '',
          stops: Array.isArray(te.additionalStops) ? te.additionalStops : [],
        });
      }
      setError(null);
    } catch (e) {
      if (e.response?.status === 403) {
        setError('This job is not assigned to you.');
      } else {
        setError(e.response?.status === 404 ? 'Booking not found' : 'Failed to load booking');
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const saveExtras = async () => {
    setSavingExtras(true);
    try {
      await driverApi.post(`/driver/${bookingId}/extras`, {
        waitingMinutes: parseFloat(extras.waitingMinutes) || 0,
        extraMiles: parseFloat(extras.extraMiles) || 0,
        notes: extras.notes || '',
        additionalStops: (extras.stops || []).filter((s) => (s.location || s.note || '').trim()),
      });
      toast.success('Trip extras saved · dispatch notified');
      await load();
    } catch (e) {
      toast.error('Could not save trip extras');
    } finally {
      setSavingExtras(false);
    }
  };

  const addExtraStop = () => setExtras((p) => ({ ...p, stops: [...(p.stops || []), { location: '', note: '' }] }));
  const updateExtraStop = (i, k, v) => setExtras((p) => ({ ...p, stops: p.stops.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const removeExtraStop = (i) => setExtras((p) => ({ ...p, stops: p.stops.filter((_, idx) => idx !== i) }));

  const handleLogout = async () => {
    await driverLogout();
    navigate('/driver/login', { replace: true });
  };

  useEffect(() => { load(); }, [load]);

  // GPS sharing
  useEffect(() => {
    const status = data?.booking?.driverStatus;
    const ACTIVE = ['on_the_way', 'arrived', 'pob'];

    const stop = () => {
      if (watchRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = null;
      }
    };

    if (!ACTIVE.includes(status)) {
      stop();
      setGpsState('inactive');
      return;
    }
    if (!('geolocation' in navigator)) {
      setGpsState('unsupported');
      return;
    }

    setGpsState('starting');
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsState('active');
        const now = Date.now();
        if (now - lastPingRef.current < 25000) return;
        lastPingRef.current = now;
        driverApi.post(`/driver/${bookingId}/location`, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
        }).catch(() => {});
      },
      () => setGpsState('denied'),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
    );
    return stop;
  }, [data?.booking?.driverStatus, bookingId]);

  const handleStatus = async (newStatus) => {
    const flowItem = STATUS_FLOW.find(s => s.key === newStatus);
    if (!flowItem) return;
    if (!window.confirm(`Confirm: "${flowItem.label}"?`)) return;
    setBusy(true);
    try {
      await driverApi.post(`/driver/${bookingId}/status`, { status: newStatus });
      toast.success(`Status updated · ${flowItem.label}`);
      await load();
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const b = data?.booking;
  const d = data?.driver;

  const customerName = useMemo(
    () => [b?.firstName, b?.lastName].filter(Boolean).join(' ').trim(),
    [b]
  );

  const currentStatus = b?.driverStatus || null;
  const currentIdx = currentStatus ? statusOrder.indexOf(currentStatus) : -1;

  const navUrlsMemo = useMemo(() => {
    if (navDestination) return navUrls(navDestination);
    const dropoff = b?.dropoffLocation || '';
    const asDir = !!b?.asDirectedHours || b?.serviceType === 'As Directed Service' || dropoff.toLowerCase().startsWith('as directed');
    const fallback = asDir ? (b?.pickupLocation || '') : (dropoff || b?.pickupLocation || '');
    return navUrls(fallback);
  }, [navDestination, b?.pickupLocation, b?.dropoffLocation, b?.asDirectedHours, b?.serviceType]);

  // Ordered destination list (pickup → stops → dropoff). As-Directed jobs have
  // no real drop-off (it's a "As Directed - N hours" placeholder), so skip it.
  const isAsDirected = !!b?.asDirectedHours
    || b?.serviceType === 'As Directed Service'
    || (b?.dropoffLocation || '').toLowerCase().startsWith('as directed');
  const destinations = useMemo(() => {
    if (!b) return [];
    const list = [];
    if (b.pickupLocation)  list.push({ key: 'pickup',  label: 'Pickup',    address: b.pickupLocation });
    (Array.isArray(b.stopovers) ? b.stopovers : []).forEach((s, i) => {
      if (s) list.push({ key: `stop-${i}`, label: `Stop ${i + 1}`, address: s });
    });
    if (!isAsDirected && b.dropoffLocation) list.push({ key: 'dropoff', label: 'Drop-off', address: b.dropoffLocation });
    return list;
  }, [b, isAsDirected]);

  // Smart suggested destination based on driver status:
  //  - before "arrived" → pickup
  //  - after "arrived" → first stopover, else dropoff
  //  - after "pob" → first stopover (if not yet visited), else dropoff
  const suggestedDestination = useMemo(() => {
    if (!destinations.length) return null;
    if (currentStatus === 'arrived' || currentStatus === 'pob') {
      const firstStop = destinations.find((d) => d.key.startsWith('stop-'));
      if (firstStop && currentStatus === 'arrived') return firstStop;
      const drop = destinations.find((d) => d.key === 'dropoff');
      return drop || destinations[destinations.length - 1];
    }
    return destinations[0];
  }, [destinations, currentStatus]);

  const openNavigateTo = (address) => {
    if (!address) return;
    setNavDestination(address);
    setShowNav(true);
  };

  // For As-Directed there is no real drop-off — use the last known stop (or
  // just the pickup) so the static map & route link don't break.
  const routeDropoff = useMemo(() => {
    if (!destinations.length) return b?.pickupLocation || '';
    return destinations[destinations.length - 1].address;
  }, [destinations, b?.pickupLocation]);

  const staticUrl = useMemo(
    () => staticMapUrl(b?.pickupLocation, routeDropoff, process.env.REACT_APP_GOOGLE_MAPS_API_KEY),
    [b?.pickupLocation, routeDropoff]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (error || !b) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-6 text-center">
        <AlertCircle className="h-10 w-10 text-neutral-400 mb-3" />
        <p className="text-lg font-semibold text-black">{error || 'Booking not found'}</p>
        <p className="text-sm text-neutral-500 mt-1">Please contact dispatch.</p>
      </div>
    );
  }

  const fr = flightRadarUrl(b.flightNumber);


  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="driver-portal">
      <style>{`
        .contact-pill {
          display:flex;align-items:center;justify-content:center;gap:.4rem;
          padding:.65rem .5rem;border-radius:.85rem;font-size:.85rem;font-weight:600;
          transition:transform .15s ease,opacity .15s ease;
        }
        .contact-pill:active { transform: scale(.96); }
      `}</style>

      {/* Hero */}
      <div className="bg-black text-white px-5 sm:px-8 pb-9" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1.25rem)' }}>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigate('/driver')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm"
              data-testid="dp-back-to-jobs"
            >
              <ArrowLeft className="h-4 w-4" /> Jobs
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="text-white/60 hover:text-white p-1"
              aria-label="Logout"
              data-testid="dp-logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-white/50">Obsidian</p>
              <p className="text-sm font-semibold tracking-wide">Driver Portal</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Booking</p>
              <p className="font-mono text-xs text-white/90" data-testid="dp-booking-id">{b.id}</p>
            </div>
          </div>

          <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">Pickup</p>
          <p className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight" data-testid="dp-pickup-time">
            {b.pickupTime}
          </p>
          <p className="text-sm text-white/70 mt-1">{formatUkDate(b.pickupDate)}</p>

          {/* GPS status pill */}
          <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-wider bg-white/10 backdrop-blur px-3 py-1.5 rounded-full">
            <span className={`h-2 w-2 rounded-full ${gpsState === 'active' ? 'bg-emerald-400 animate-pulse' : gpsState === 'denied' ? 'bg-red-400' : 'bg-white/40'}`} />
            <span className="text-white/80">
              {gpsState === 'active'      ? 'Live GPS sharing'
               : gpsState === 'starting'  ? 'Acquiring GPS…'
               : gpsState === 'denied'    ? 'Location permission needed'
               : gpsState === 'unsupported' ? 'GPS unsupported'
               : 'GPS starts at "On The Way"'}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-5 sm:px-8 -mt-5 space-y-4">

        {/* Action bar */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => suggestedDestination && openNavigateTo(suggestedDestination.address)}
            disabled={!suggestedDestination}
            className="bg-black text-white rounded-2xl py-4 px-4 flex flex-col items-center justify-center gap-0.5 font-semibold shadow-lg hover:bg-neutral-900 disabled:opacity-50"
            data-testid="dp-btn-navigate"
          >
            <span className="flex items-center gap-2"><Navigation2 className="h-5 w-5" /> Navigate</span>
            {suggestedDestination && (
              <span className="text-[10px] uppercase tracking-wider text-white/60 font-medium">
                to {suggestedDestination.label}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowSign(true)}
            className="bg-white border-2 border-black text-black rounded-2xl py-4 px-4 flex items-center justify-center gap-2 font-semibold hover:bg-black hover:text-white"
            data-testid="dp-btn-name-sign"
          >
            <IdCard className="h-5 w-5" /> Name Sign
          </button>
        </div>

        {/* Add to calendar */}
        <a
          href={`${API}/bookings/${b.id}/calendar.ics`}
          className="w-full bg-white border border-neutral-200 rounded-2xl py-3.5 px-4 flex items-center justify-center gap-2 font-semibold text-black hover:bg-neutral-50"
          data-testid="dp-btn-add-calendar"
        >
          <CalendarPlus className="h-5 w-5" /> Add to my Calendar
        </a>

        {/* Customer */}
        <ContactRow name={customerName} phone={b.phone} />
        {b.bookerName && (
          <div className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-5" data-testid="dp-booker-row">
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Booked by</p>
            <p className="font-semibold text-black text-sm" data-testid="dp-booker-name">{b.bookerName}</p>
            {b.bookerPhone && (
              <p className="text-xs text-neutral-600 mt-0.5">
                <a href={`tel:${b.bookerPhone}`} className="hover:underline" data-testid="dp-booker-phone">
                  {formatDisplay(b.bookerPhone) || b.bookerPhone}
                </a>
              </p>
            )}
          </div>
        )}

        {/* Journey */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-3">Journey</p>

          <div className="space-y-3">
            {destinations.map((dest, i) => {
              const isPickup  = dest.key === 'pickup';
              const isDropoff = dest.key === 'dropoff';
              const dotCls = isPickup
                ? 'h-3 w-3 rounded-full bg-emerald-500'
                : isDropoff
                  ? 'h-3 w-3 rounded-sm bg-red-600'
                  : 'h-2.5 w-2.5 rounded-full bg-amber-500';
              return (
                <div key={dest.key} className="flex items-start gap-3" data-testid={`dp-journey-row-${dest.key}`}>
                  <div className="flex flex-col items-center pt-1.5 shrink-0">
                    <div className={dotCls} />
                    {i < destinations.length - 1 && (
                      <div className="w-px h-8 bg-neutral-300 mt-1" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(dest.address, dest.label)}
                    className="flex-1 min-w-0 text-left -mx-1 px-1 py-0.5 rounded-md hover:bg-neutral-50 active:bg-neutral-100 group"
                    data-testid={`dp-copy-address-${dest.key}`}
                    title="Tap to copy address"
                  >
                    <p className="text-[10px] uppercase tracking-wider text-neutral-400">{dest.label}</p>
                    <p className="text-sm font-semibold text-black break-words leading-snug inline-flex items-start gap-1.5">
                      <span>{dest.address}</span>
                      <Copy className="h-3 w-3 text-neutral-300 group-hover:text-neutral-500 shrink-0 mt-1" />
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => openNavigateTo(dest.address)}
                    className="shrink-0 h-9 w-9 rounded-full bg-neutral-100 hover:bg-black hover:text-white text-neutral-700 flex items-center justify-center transition-colors"
                    aria-label={`Navigate to ${dest.label}`}
                    data-testid={`dp-row-nav-${dest.key}`}
                  >
                    <Navigation2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {staticUrl && (
            <a
              href={navUrls(routeDropoff, b.pickupLocation).google}
              target="_blank"
              rel="noreferrer"
              className="block mt-4 rounded-xl overflow-hidden border border-neutral-200"
              data-testid="dp-static-map"
            >
              <img src={staticUrl} alt="Route map" className="w-full h-auto block" />
            </a>
          )}

          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-neutral-100">
            {b.calculatedDistance && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400">Distance</p>
                <p className="text-sm font-semibold text-black">{b.calculatedDistance}</p>
              </div>
            )}
            {b.calculatedTime && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400">Est. Time</p>
                <p className="text-sm font-semibold text-black">{b.calculatedTime}</p>
              </div>
            )}
          </div>
        </div>

        {/* Flight */}
        {b.flightNumber && (
          <a
            href={fr}
            target="_blank"
            rel="noreferrer"
            className="bg-white border border-neutral-200 rounded-2xl p-5 flex items-center gap-4 hover:border-black hover:shadow-md transition-all"
            data-testid="dp-flight-card"
          >
            <div className="h-12 w-12 rounded-xl bg-black flex items-center justify-center shrink-0">
              <Plane className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Flight</p>
              <p className="font-bold text-black text-lg tracking-wide">{cleanFlightNumber(b.flightNumber)}</p>
              <p className="text-xs text-neutral-500">Tap to track on FlightRadar24</p>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
          </a>
        )}

        {/* Service & Vehicle */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-5 grid grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Service</p>
            <p className="font-semibold text-black mt-1">{b.serviceType || '—'}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Vehicle</p>
            <p className="font-semibold text-black mt-1 flex items-center gap-1.5">
              <Car className="h-4 w-4 text-neutral-500" /> {b.vehicleName || '—'}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Passengers</p>
            <p className="font-semibold text-black mt-1 flex items-center gap-1.5">
              <Users className="h-4 w-4 text-neutral-500" /> {b.passengers || 0}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Luggage</p>
            <p className="font-semibold text-black mt-1 flex items-center gap-1.5">
              <Briefcase className="h-4 w-4 text-neutral-500" /> {b.luggage || 0}
            </p>
          </div>
        </div>

        {/* Special requests */}
        {b.specialRequests && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="dp-special-requests">
            <p className="text-[11px] uppercase tracking-[0.18em] text-amber-800 mb-1.5">Special Requests</p>
            <p className="text-sm text-amber-950 leading-relaxed">{b.specialRequests}</p>
          </div>
        )}

        {/* Driver info */}
        {d && (
          <div className="bg-white border border-neutral-200 rounded-2xl p-5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-black text-white flex items-center justify-center text-sm font-bold">
              {(d.name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Assigned Driver</p>
              <p className="font-semibold text-black truncate">{d.name}</p>
              {d.vehicle && <p className="text-xs text-neutral-500 truncate">{d.vehicle}</p>}
            </div>
          </div>
        )}

        {/* Status flow */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-5">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-3 flex items-center gap-2">
            <Clock className="h-3 w-3" /> Job Status
          </p>
          <div className="space-y-2.5" data-testid="dp-status-flow">
            {STATUS_FLOW.map((s, i) => (
              <StatusButton
                key={s.key}
                s={s}
                idx={i}
                currentIdx={currentIdx}
                onClick={handleStatus}
                busy={busy}
              />
            ))}
          </div>
        </div>

        {/* Trip Extras — driver logs waiting time / extra stops / mileage */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid="dp-trip-extras">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-1 flex items-center gap-2">
            <Timer className="h-3 w-3" /> Trip Extras
          </p>
          <p className="text-xs text-neutral-500 mb-4">Log any waiting time, extra stops or mileage. Dispatch uses this to bill the customer.</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-neutral-400">Waiting (min)</label>
              <input
                type="number" min="0" inputMode="numeric" placeholder="0"
                value={extras.waitingMinutes}
                onChange={(e) => setExtras((p) => ({ ...p, waitingMinutes: e.target.value }))}
                className="w-full mt-1 h-11 border border-neutral-200 rounded-xl px-3 text-base focus:outline-none focus:border-black"
                data-testid="dp-extra-waiting"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-neutral-400">Extra miles</label>
              <input
                type="number" min="0" inputMode="decimal" placeholder="0"
                value={extras.extraMiles}
                onChange={(e) => setExtras((p) => ({ ...p, extraMiles: e.target.value }))}
                className="w-full mt-1 h-11 border border-neutral-200 rounded-xl px-3 text-base focus:outline-none focus:border-black"
                data-testid="dp-extra-miles"
              />
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-neutral-400">Additional stops</label>
            {(extras.stops || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`dp-extra-stop-${i}`}>
                {gmapsLoaded ? (
                  <Autocomplete
                    onLoad={(ac) => { stopAcRefs.current[i] = ac; }}
                    onPlaceChanged={() => {
                      const place = stopAcRefs.current[i]?.getPlace();
                      const addr = placeLabel(place);
                      if (addr) updateExtraStop(i, 'location', addr);
                    }}
                    className="flex-1"
                  >
                    <input
                      placeholder="Search stop address"
                      value={s.location || ''}
                      onChange={(e) => updateExtraStop(i, 'location', e.target.value)}
                      className="w-full h-11 border border-neutral-200 rounded-xl px-3 text-sm focus:outline-none focus:border-black"
                      data-testid={`dp-extra-stop-input-${i}`}
                    />
                  </Autocomplete>
                ) : (
                  <input
                    placeholder="Stop location / note"
                    value={s.location || ''}
                    onChange={(e) => updateExtraStop(i, 'location', e.target.value)}
                    className="flex-1 h-11 border border-neutral-200 rounded-xl px-3 text-sm focus:outline-none focus:border-black"
                    data-testid={`dp-extra-stop-input-${i}`}
                  />
                )}
                <button type="button" onClick={() => removeExtraStop(i)} className="h-11 w-11 shrink-0 rounded-xl bg-neutral-100 text-neutral-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center" aria-label="Remove stop" data-testid={`dp-extra-stop-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={addExtraStop} className="inline-flex items-center gap-1.5 text-xs font-semibold text-black hover:text-neutral-600" data-testid="dp-extra-add-stop">
              <Plus className="h-3.5 w-3.5" /> Add stop
            </button>
          </div>

          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-wider text-neutral-400">Notes</label>
            <textarea
              rows={2} placeholder="Anything dispatch should know…"
              value={extras.notes}
              onChange={(e) => setExtras((p) => ({ ...p, notes: e.target.value }))}
              className="w-full mt-1 border border-neutral-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-black resize-none"
              data-testid="dp-extra-notes"
            />
          </div>

          <button
            type="button" onClick={saveExtras} disabled={savingExtras}
            className="mt-4 w-full bg-black text-white rounded-xl py-3 font-semibold hover:bg-neutral-900 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="dp-extra-save"
          >
            {savingExtras ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {savingExtras ? 'Saving…' : 'Save trip extras'}
          </button>
        </div>

        <p className="text-center text-[11px] text-neutral-400 pt-3">
          Obsidian Chauffeurs · Confidential
        </p>
      </div>

      <NavSheet
        open={showNav}
        onClose={() => setShowNav(false)}
        destination={navDestination}
        urls={navUrlsMemo}
      />
      {showSign && (
        <NameSignOverlay name={customerName} onClose={() => setShowSign(false)} />
      )}
    </div>
  );
};

export default DriverPortal;
